<template>
    <div class="relative">
        <label class="relative block" ref="reference">
            <input
                class="h-10 w-full flex items-center gap-2 text-left pl-3 text-slate-500 dark:text-slate-400 relative bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 py-1.5 placeholder:text-slate-500 dark:text-slate-400 placeholder:text-sm transition-colors text-sm focus:ring-0 focus:border-slate-800 hover:border-slate-800 disabled:opacity-50 disabled:cursor-not-allowed mb-2"
                v-model="programQuery"
                @focus="openDropdown"
                @click="openDropdown"
                :class="tripFormErrors?.transfer_program_id ? '!border-red-500' : ''"
            />
            <span class="w-[90%] flex items-center text-sm gap-2 pr-14 absolute left-2 top-1/2 -translate-y-1/2 whitespace-nowrap truncate">
                <ImageLoader v-if="programChosen">
                    <template #image>
                        <img
                            :src="`${cdnUrl}/${programChosen?.program_image?.path}/_small/${programChosen?.program_image?.file_name}`"
                            :alt="programChosen?.name"
                            class="max-w-10 max-h-5 rounded-sm"
                        />
                    </template>
                    <template #error>
                        <Icon icon="points" class="text-slate-600 dark:text-slate-300 w-8 h-5" />
                    </template>
                </ImageLoader>
                {{ programChosen?.name }}
            </span>
            <button
                class="block leading-none text-red-600 w-5 h-5 absolute right-2 top-1/2 -translate-y-1/2 z-10"
                v-if="programChosen"
                @click="clearProgram()"
            >
                <Icon icon="close" />
            </button>
        </label>
        <div
            class="text-red-500 text-xs flex items-center gap-2 py-1.5"
            role="alert"
            v-if="tripFormErrors?.transfer_program_id"
        >
            <Icon icon="caution" />
            Please choose a transfer points currency
        </div>
        <Teleport to="body">
            <transition name="fade">
                <div
                    class="max-h-48 p-2 overflow-y-auto bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 absolute z-[99999]"
                    v-if="showList"
                    ref="floating"
                >
                    <label
                        class="w-full flex items-center gap-2 px-3 py-2 text-slate-800 dark:text-slate-100 text-sm bg-white dark:bg-slate-800 rounded hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors mb-1 cursor-pointer"
                        v-for="program in transferProgramsFiltered"
                        :key="program.id"
                        @click="selectProgram(program)"
                    >
                        <input
                            class="sr-only"
                            type="radio"
                            name="transfer_program_id"
                            :value="program.id"
                            :checked="programChosen?.id === program.id"
                        />
                        <div class="w-12 flex justify-center">
                            <ImageLoader>
                                <template #image>
                                    <img
                                        :src="`${cdnUrl}/${program.image?.path}/_small/${program.image?.file_name}`"
                                        :alt="program?.label"
                                        class="max-w-10 max-h-5 rounded-sm"
                                    />
                                </template>
                                <template #error>
                                    <Icon icon="points" class="text-slate-600 dark:text-slate-300 w-8 h-5" />
                                </template>
                            </ImageLoader>
                        </div>
                        {{ program?.label }}
                    </label>
                    <div
                        v-if="transferProgramsFiltered.length === 0"
                        class="px-3 py-2 text-sm text-slate-800"
                    >
                        No programs found
                    </div>
                </div>
            </transition>
        </Teleport>
    </div>
</template>
<script setup>
import { storeToRefs } from 'pinia';
import {
    onMounted,
    onUnmounted,
    ref,
    watch,
    computed,
    nextTick,
} from 'vue';
import { useTripsStore } from '@/store/trips';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { computePosition, flip, shift, offset } from '@floating-ui/vue';
import Icon from '@/components/ui/icon/icon.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';

const props = defineProps({
    currentProgramId: {
        type: Number,
        default: null
    },
    tripFormErrors: {
        type: Object,
        default: () => ({})
    }
});

const emit = defineEmits(['update:currentProgramId']);

const cdnUrl = computed(() => {
    return window.cdnUrl;
});

const tripsStore = useTripsStore();
const { programs, activeRedemption, transferPartners } = storeToRefs(tripsStore);
const { transferPartnersGuideEnabled } = useFeatureFlags();

const programQuery = ref(null);
const showList = ref(false);
const reference = ref(null);
const floating = ref(null);

const programChosen = computed(() => {
    if (!props.currentProgramId) return null;
    return programs.value.find(p => p.id === props.currentProgramId);
});

const selectProgram = (program) => {
    emit('update:currentProgramId', program.id);
    showList.value = false;
    programQuery.value = null;
};

const clearProgram = () => {
    emit('update:currentProgramId', null);
};

const handleGlobalClick = (event) => {
    if (
        (floating.value && floating.value.contains(event.target)) ||
        (reference.value && reference.value.contains(event.target))
    ) {
        return;
    }

    showList.value = false;
};

const openDropdown = () => {
    showList.value = true;
};

const updatePosition = () => {
    if (reference.value && floating.value) {
        computePosition(reference.value, floating.value, {
            placement: 'bottom-start',
            middleware: [offset(4), flip(), shift()],
        }).then(({ x, y }) => {
            // Get the reference element's width to match the dropdown width
            const referenceWidth = reference.value.offsetWidth;

            Object.assign(floating.value.style, {
                left: `${x}px`,
                top: `${y}px`,
                width: `${referenceWidth}px`,
            });
        });
    }
};

watch(
    () => showList.value,
    (newValue) => {
        if (newValue) {
            nextTick(() => {
                updatePosition();
            });
        }
    }
);

const transferProgramsFiltered = computed(() => {
    const allFlexible = programs.value
        .map((program) => {
            return {
                label: program.name,
                value: program.type,
                id: program.id,
                image: program.program_image,
            };
        })
        .filter((program) => program.value === 'flexible_currency');

    const destinationId = activeRedemption.value?.payment_program_id;
    const partners = transferPartners.value || [];

    // If we have transfer partners data and a destination selected, restrict to valid sources.
    // Gated behind transfer-partners-guide: when off, fall back to listing all flexible currencies.
    let filteredByPartners = allFlexible;
    if (transferPartnersGuideEnabled.value && destinationId && partners.length > 0) {
        const allowedSourceIds = new Set(
            partners
                .filter((tp) => tp?.destination_program_id === destinationId)
                .map((tp) => Number(tp?.source_program_id))
                .filter((id) => !!id)
        );
        filteredByPartners = allFlexible.filter((p) => allowedSourceIds.has(Number(p.id)));
    }

    return filteredByPartners.filter((program) => {
        return programQuery.value && programQuery.value.length > 0
            ? program.label
                  .toLowerCase()
                  .replace('.', '')
                  .includes(programQuery.value.toLowerCase())
            : true;
    });
});

onMounted(() => {
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    document.addEventListener('click', handleGlobalClick);
    document.addEventListener('touchend', handleGlobalClick);
});

onUnmounted(() => {
    window.removeEventListener('scroll', updatePosition, true);
    window.removeEventListener('resize', updatePosition);
    document.removeEventListener('click', handleGlobalClick);
    document.removeEventListener('touchend', handleGlobalClick);
});
</script>
